import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageTransition } from "@/components/PageTransition";
import {
  ThreeWaysSection,
  TrainingDataSection,
  TestimonialsSection,
  Eyebrow,
  SectionHeading,
} from "@/components/get-found/GetFoundSections";
import { ResultsSection } from "@/components/get-found/GetFoundResults";
import { QualifyingFormSection } from "@/components/get-found/QualifyingForm";
import {
  ChevronDown,
  ArrowRight,
  Check,
  FileText,
  ScanSearch,
  Sparkles,
  CalendarDays,
  MapPin,
  Bot,
} from "lucide-react";

export const Route = createFileRoute("/get-found/")({
  head: () => ({
    meta: [
      {
        title:
          "Get Found Playbook: SEO + AI Search Prompts | The Outpost Creative",
      },
      {
        name: "description",
        content:
          "The prompt playbook that gets your business found on Google, Google Maps and ChatGPT. 28-page playbook, 3 comprehensive audits, and 20 personalized AI prompts. $47 one-time.",
      },
      {
        property: "og:title",
        content: "Get Found Playbook — The Outpost Creative",
      },
      {
        property: "og:description",
        content:
          "Copy-paste AI prompts and a personalized audit of your website: every fix ranked, every prompt pre-filled with your business. No agency required.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://theoutpost.agency/get-found" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Get Found Playbook + 3 Comprehensive Audits",
          description:
            "The Get Found Playbook (PDF) plus an access code good for 3 comprehensive SEO audits.",
          brand: { "@type": "Brand", name: "The Outpost Creative" },
          offers: {
            "@type": "Offer",
            price: "47.00",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
          },
        }),
      },
    ],
  }),
  component: GetFoundRoute,
});

const BOOKING_URL =
  "https://api.leadconnectorhq.com/widget/booking/DYjiazsS0izL2JgzT2a8";

function GetFoundRoute() {
  return (
    <PageTransition>
      <div className="min-h-screen bg-[#0a0a0f] text-white selection:bg-[#b85736] selection:text-black">
        <Navbar />
        <main className="pt-16">
          <Hero />
          <TrainingDataSection />
          <ResultsSection />
          <TestimonialsSection />
          <ThreeWaysSection />
          <Problem />
          <WhatYouGet />
          <HowItWorks />
          <InsidePlaybook />
          <QualifyingFormSection />
          <Faq />
          <FinalResultsCta />
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
}

function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {
      /* autoplay blocked; controls let the user start it */
    });
  }, []);
  return (
    <section className="relative flex flex-col items-center justify-start px-4 md:px-5 pt-8 md:pt-12 pb-16 text-center overflow-hidden">
      {/* Background glow effects matching jeremyhaynesai styling */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute top-[10%] left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full opacity-30"
          style={{
            background:
              "radial-gradient(circle, rgba(218, 111, 77, 0.25) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute top-[5%] left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full opacity-20"
          style={{
            background:
              "radial-gradient(circle, rgba(255, 180, 120, 0.2) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255, 255, 255, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto space-y-6">
        {/* Pill Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-sm">
          <span className="flex gap-1 flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-[#b85736] animate-pulse" />
            <span
              className="w-1.5 h-1.5 rounded-full bg-orange-300 animate-pulse"
              style={{ animationDelay: "0.3s" }}
            />
          </span>
          <span className="font-mono text-[11px] text-white/70 tracking-[0.15em] uppercase font-medium">
            Getting direct access to high-ranking SEO & AI search has never been
            this easy
          </span>
        </div>

        {/* Main Headline */}
        <div className="space-y-2">
          <h1 className="text-[clamp(2.2rem,6vw,5rem)] font-extrabold tracking-tight leading-[1.02] text-white uppercase font-heading">
            The exact SOP behind{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #b85736 0%, #f6ddd2 50%, #b85736 100%)",
              }}
            >
              $100K+/mo Local Founders
            </span>{" "}
            <span className="block text-white/80 normal-case font-normal italic font-serif text-[0.85em]">
              personalized for your business.
            </span>
          </h1>
        </div>

        {/* Subhead */}
        <p className="max-w-2xl mx-auto text-lg md:text-xl text-white/80 leading-relaxed font-light">
          Agency SEO retainers cost{" "}
          <strong className="text-white font-semibold">$3K–$10K/mo</strong>. The
          Get Found Playbook gives you a complete 28-page system, 3 live website
          audits, and 20 personalized AI prompts —{" "}
          <span className="text-white">
            everything you need to dominate Google, Maps, and ChatGPT for $47.
          </span>
        </p>

        {/* Video / Card Mockup Section */}
        <div className="w-full max-w-4xl mx-auto pt-4">
          <div
            className="rounded-2xl overflow-hidden border border-white/10 text-left"
            style={{
              background: "#111118",
              boxShadow:
                "0 0 80px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.05)",
            }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08] bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#b85736]" />
                <span className="font-mono text-[11px] text-white/60 tracking-widest uppercase font-medium">
                  our exact agency sop
                </span>
              </div>
              <span className="font-mono text-[11px] text-white/40 tracking-wider">
                28 PAGES · 20 PROMPTS · 3 AUDITS
              </span>
            </div>

            <div className="p-6 md:p-10 grid md:grid-cols-2 gap-8 items-center bg-gradient-to-b from-[#151515] to-[#0d0d12]">
              <div className="space-y-4">
                <div className="inline-block px-3 py-1 bg-[#b85736]/20 border border-[#b85736]/40 rounded font-mono text-xs text-[#b85736] uppercase tracking-wider font-bold">
                  Included in Playbook
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold uppercase text-white leading-tight">
                  Stop Paying Agencies Thousands For Basic SEO Fixes
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  See how our 14-day execution map and pre-filled prompt stack
                  turns your website into a top-ranking local client magnet.
                </p>
                <div className="pt-2 flex items-center gap-4 text-xs font-mono text-white/50">
                  <span className="flex items-center gap-1.5">
                    <Check className="h-4 w-4 text-[#b85736]" /> 100 SEO Score
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check className="h-4 w-4 text-[#b85736]" /> Core 30 Pages
                  </span>
                </div>
              </div>

              {/* Video inside hero */}
              <div className="relative rounded-xl border border-white/10 overflow-hidden shadow-2xl bg-black aspect-video">
                <video
                  ref={videoRef}
                  src="https://assets.cdn.filesafe.space/rizeckkMPBYZT9pK9UjX/media/6abeb0d11325c552eecf54f5.mp4"
                  className="absolute inset-0 w-full h-full object-contain"
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                  preload="auto"
                />
              </div>
            </div>

            <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.08] bg-black/40">
              <span className="text-white/60 text-xs md:text-sm">
                Complete system for Google Maps, Search, and ChatGPT
              </span>
              <span className="font-mono text-white/40 text-xs">
                Instant PDF Download
              </span>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="pt-4 space-y-3">
          <a
            href="#qualify"
            className="inline-flex items-center justify-center gap-3 font-bold text-lg px-8 py-4 rounded-xl transition-all duration-150 text-[#151515] bg-[#b85736] hover:bg-[#eee9e3] w-full sm:w-auto uppercase tracking-wider"
            style={{
              boxShadow: "0 0 35px rgba(218, 111, 77, 0.35)",
            }}
          >
            Get the Playbook — $47 <ArrowRight className="h-5 w-5" />
          </a>
          <p className="font-mono text-xs text-white/50">
            Instant digital delivery · Includes 3 comprehensive audits of your
            site
          </p>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  const items = [
    {
      icon: MapPin,
      title: "You're invisible on the map.",
      body: "60–70% of local clicks go to the top 3 Google Maps results. If you're not there, you don't exist.",
    },
    {
      icon: FileText,
      title: "Your website isn't pulling its weight.",
      body: 'Most local sites still have "Home" as their page title, and Google has no idea what they do or where.',
    },
    {
      icon: Bot,
      title: "AI is the new front door.",
      body: 'People now ask ChatGPT "who\'s the best [service] near me?" and most businesses never get mentioned.',
    },
  ];
  return (
    <section className="bg-[#111118] border-y border-white/10 px-5 md:px-8 py-16 md:py-24">
      <div className="max-w-5xl mx-auto">
        <Eyebrow>The Problem</Eyebrow>
        <SectionHeading className="text-white">
          Your customers are searching. Are they finding you?
        </SectionHeading>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {items.map((it) => (
            <div
              key={it.title}
              className="bg-black/50 border-l-4 border-[#b85736] p-6 space-y-3 rounded-r-lg border-y border-r border-white/5"
            >
              <it.icon className="h-7 w-7 text-[#b85736]" />
              <h3 className="font-heading font-extrabold text-lg uppercase tracking-tight text-white">
                {it.title}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatYouGet() {
  const items = [
    {
      icon: FileText,
      title: "The Get Found Playbook (28 pages).",
      body: 'The step-by-step system: Google Search Console setup, getting your site to a 100 SEO score, Google Business Profile optimization, the "Core 30" local page structure, and getting recommended by AI.',
    },
    {
      icon: ScanSearch,
      title: "3 Comprehensive Audits of your website.",
      body: "Google's own 100-point checklist, AI readiness (can ChatGPT read your site?), a Google Business Profile scorecard, and your missing local pages. Run one now, one after your fixes, and one to prove the progress.",
    },
    {
      icon: Sparkles,
      title: "20 AI prompts, personalized to you.",
      body: "Your audit fills every prompt with your business name, services, city, competitors and actual results. Copy into ChatGPT or Claude and get finished title tags, service pages, 52 weeks of Google posts and more.",
    },
    {
      icon: CalendarDays,
      title: "A 14-day action plan.",
      body: "Day by day, built around the gaps your audit finds.",
    },
  ];
  return (
    <section className="px-5 md:px-8 py-16 md:py-24 max-w-5xl mx-auto">
      <Eyebrow>What You Get</Eyebrow>
      <SectionHeading className="text-white mt-2">
        Everything you need to fix it yourself
      </SectionHeading>
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {items.map((it) => (
          <div
            key={it.title}
            className="bg-[#111118] border border-white/10 p-6 space-y-3 rounded-xl hover:border-[#b85736]/40 transition-colors"
          >
            <it.icon className="h-7 w-7 text-[#b85736]" />
            <h3 className="font-heading font-extrabold text-lg uppercase tracking-tight text-white">
              {it.title}
            </h3>
            <p className="text-white/70 text-sm leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "1",
      title: "Audit.",
      body: "Enter your code, tell us about your business, and get your full report in about a minute.",
    },
    {
      n: "2",
      title: "Fix.",
      body: "Work your personalized action plan. Every issue links to the exact prompt that fixes it.",
    },
    {
      n: "3",
      title: "Rescan.",
      body: "Use your next audit to see your scores move.",
    },
  ];
  return (
    <section className="bg-[#111118] border-y border-white/10 text-white px-5 md:px-8 py-16 md:py-24">
      <div className="max-w-5xl mx-auto">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#b85736] font-bold">
          How It Works
        </span>
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight mt-3">
          3 steps. No guesswork.
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div key={s.n} className="space-y-3">
              <span className="font-heading font-extrabold text-5xl text-[#b85736]">
                {s.n}
              </span>
              <h3 className="font-heading font-extrabold text-xl uppercase tracking-tight text-white">
                {s.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InsidePlaybook() {
  const items = [
    "How Google and AI search actually work (2 minutes)",
    "Set up Search Console, Bing and AI traffic tracking",
    "Get your site to a 100 SEO score",
    "Win the Google Map: categories, services, reviews, posts",
    "The Core 30: make your website rank your Google Business Profile",
    "Get recommended by ChatGPT and Google's AI",
    "Your 30-day game plan + the Master Prompt",
    "The don't-do list (what gets businesses penalized)",
  ];
  return (
    <section className="px-5 md:px-8 py-16 md:py-24 max-w-5xl mx-auto">
      <Eyebrow>Inside the Playbook</Eyebrow>
      <SectionHeading className="text-white mt-2">
        What's covered
      </SectionHeading>
      <ul className="mt-10 grid md:grid-cols-2 gap-4">
        {items.map((it) => (
          <li
            key={it}
            className="flex items-start gap-3 bg-[#111118] border border-white/10 p-4 rounded-lg"
          >
            <Check className="h-5 w-5 text-[#b85736] mt-0.5 shrink-0" />
            <span className="text-white/80 text-sm">{it}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function OrderSection() {
  return (
    <section
      id="order"
      className="bg-[#111118] border-y border-white/10 px-5 md:px-8 py-16 md:py-24 scroll-mt-24 text-white"
    >
      <div className="max-w-xl mx-auto text-center space-y-6">
        <Eyebrow>Order</Eyebrow>
        <SectionHeading className="text-white mt-1">
          Get instant access: $47
        </SectionHeading>
        <p className="text-white/70 text-sm md:text-base">
          One-time payment. Playbook + 3 comprehensive audits + 20 personalized
          prompts.
        </p>
        <a
          href="#qualify"
          className="inline-flex items-center justify-center gap-3 font-bold text-lg px-8 py-4 rounded-xl transition-all duration-150 text-[#151515] bg-[#b85736] hover:bg-[#eee9e3] uppercase tracking-wider"
          style={{
            boxShadow: "0 0 35px rgba(218, 111, 77, 0.35)",
          }}
        >
          Continue to secure checkout <ArrowRight className="h-5 w-5" />
        </a>
        <p className="font-mono text-xs text-white/50">
          Instant digital delivery · Includes 3 comprehensive audits of your
          site
        </p>
      </div>
    </section>
  );
}

function Faq() {
  const faqs = [
    {
      q: "Is this for my business?",
      a: "It's built for local service businesses (contractors, home services, clinics, agencies, pros) that want more calls from Google and AI search.",
    },
    {
      q: "Do I need to be technical?",
      a: "No. Every fix comes with step-by-step instructions for your website platform (GoHighLevel, WordPress, Wix, Squarespace and more).",
    },
    {
      q: "Do you guarantee rankings?",
      a: "No one honestly can. What this does is find exactly what's holding you back and give you the fixes. Most changes take about 30 days to show.",
    },
    {
      q: "What if I have more than one website?",
      a: "Each purchase covers 3 audits of one website. Grab another copy for a second site.",
    },
    {
      q: "What if I'd rather have you do it?",
      a: "Book a free strategy call and we'll walk through your audit together.",
    },
  ];
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="px-5 md:px-8 py-16 md:py-24 max-w-3xl mx-auto">
      <Eyebrow>FAQ</Eyebrow>
      <SectionHeading className="text-white mt-1">Questions</SectionHeading>
      <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
        {faqs.map((f, i) => (
          <div key={f.q}>
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between py-5 text-left"
            >
              <span className="font-heading font-extrabold text-lg uppercase tracking-tight text-white">
                {f.q}
              </span>
              <ChevronDown
                className={`h-5 w-5 text-[#b85736] transition-transform ${
                  open === i ? "rotate-180" : ""
                }`}
              />
            </button>
            {open === i && (
              <p className="pb-5 text-white/70 text-sm leading-relaxed">
                {f.a}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function FinalResultsCta() {
  return (
    <section className="bg-[#151515] text-[#eee9e3] px-5 md:px-8 py-16 md:py-24">
      <div className="max-w-3xl mx-auto text-center space-y-6">
        <h2 className="font-heading text-3xl md:text-5xl font-extrabold uppercase tracking-tight leading-[0.95]">
          Stop guessing. Start getting found.
        </h2>
        <a
          href="#qualify"
          className="inline-flex items-center gap-2 bg-[#b85736] text-[#151515] font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#eee9e3] transition-colors"
        >
          Get the Playbook — $47 <ArrowRight className="h-5 w-5" />
        </a>
        <p className="font-mono text-sm text-[#a9a097]">
          Rather have us handle it?{" "}
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#b85736] underline"
          >
            Book a free strategy call →
          </a>
        </p>
      </div>
    </section>
  );
}
